import React, { useState } from 'react';
import classNames from 'classnames';

import SimpleModal from 'components/base/SimpleModal';
import SVG from 'components/base/SVG';
import Select from 'components/Select';
import CheckboxItem from 'components/Checkbox';
import { UsStateShortOptions } from 'data/user/constants';

import css from './style.scss';

/**
 * "Contact Information" nested modal (Figma 383-26723).
 *
 * The presentation is new, but the row semantics are lifted verbatim from the
 * legacy `ContactEditor/Items` (`ContactItems`): the same seq-indexed add /
 * remove / change behaviour, the same `publicDNC` lock-out, and the same
 * `dncPab` flag where 'D' is My DNC and 'A' (PAB) must survive a toggle.
 * `ContactItems` itself is left untouched -- the legacy editor still renders it.
 */

const PHONE_TYPES = [
  { label: 'Mobile', value: 'MOBILE' },
  { label: 'Landline', value: 'LANDLINE' },
  { label: 'Other', value: 'OTHER' },
];

const EMAIL_TYPES = [
  { label: 'Home', value: 'HOME' },
  { label: 'Work', value: 'WORK' },
  { label: 'Other', value: 'OTHER' },
];

const DNC_OPTIONS = [
  { label: 'My DNC', value: 'MY_DNC' },
  { label: 'No DNC', value: 'NO_DNC' },
];

const nextSeq = (items) => items.reduce((seq, i) => Math.max(seq, i.seq ?? -1), -1) + 1;

const SectionHeader = ({ title, count, info, onAdd }) => (
  <div className={css.ciSectionHeader}>
    <div className={css.ciSectionTitle}>
      {title} ({count}){!info ? null : <SVG icon="cp-info" className={css.ciInfoIcon} />}
    </div>
    <button type="button" className={css.btnOutline} onClick={onAdd}>
      <SVG icon="cp-plus" className={css.btnOutlineIcon} />
      Add
    </button>
  </div>
);

const TrashButton = ({ onClick }) => (
  <button type="button" className={css.ciTrash} onClick={onClick} aria-label="Remove">
    <SVG icon="cp-trash" className={css.ciTrashIcon} />
  </button>
);

const TypeSelect = ({ types, value, onChange }) => (
  <div className={css.ciType}>
    <Select
      name="type"
      lists={types}
      selectedValue={value}
      valueKey="value"
      labelKey="label"
      autoFlip
      onSelect={({ value: next }) => onChange('type', next)}
    />
  </div>
);

const AddressField = ({ name, label, value, maxLength, onChange, className }) => (
  <div className={classNames(css.field, className)}>
    <div className={css.fieldLabel}>{label}</div>
    <input
      className={css.fieldInput}
      name={name}
      type="text"
      value={value || ''}
      maxLength={maxLength}
      onChange={onChange}
    />
  </div>
);

const StateField = ({ name, label, value, onChange, className }) => (
  <div className={classNames(css.field, className)}>
    <div className={css.fieldLabel}>{label}</div>
    <Select
      name={name}
      lists={UsStateShortOptions}
      selectedValue={value}
      valueKey="value"
      labelKey="label"
      autoFlip
      onSelect={({ value: next }) => onChange({ target: { name, value: next } })}
    />
  </div>
);

const AddressBlock = ({ title, prefix, draft, onChange }) => {
  const key = (suffix) =>
    prefix ? `${prefix}${suffix}` : suffix.charAt(0).toLowerCase() + suffix.slice(1);
  return (
    <section className={css.ciAddress}>
      <div className={css.ciSectionTitle}>{title}</div>
      <div className={css.ciAddressRow}>
        <AddressField
          name={key('StreetAddress')}
          label="Street"
          maxLength={200}
          value={draft[key('StreetAddress')]}
          onChange={onChange}
          className={css.ciStreet}
        />
        <AddressField
          name={key('City')}
          label="City"
          maxLength={100}
          value={draft[key('City')]}
          onChange={onChange}
          className={css.ciCity}
        />
      </div>
      <div className={css.ciAddressRow}>
        <StateField
          name={key('State')}
          label="State"
          value={draft[key('State')]}
          onChange={onChange}
          className={css.ciState}
        />
        <AddressField
          name={key('Zip')}
          label="Zip"
          maxLength={10}
          value={draft[key('Zip')]}
          onChange={onChange}
          className={css.ciZip}
        />
      </div>
    </section>
  );
};

const ContactInformationModal = ({ contact, onCancel, onSave }) => {
  const [draft, setDraft] = useState({ ...contact });

  const phones = draft.phones || [];
  const emails = draft.emails || [];

  const handleChange = (ev) => {
    const { name, value, checked } = ev.target || ev;
    setDraft((prev) => ({
      ...prev,
      [name]: name === 'mailAddressSame' ? !checked : value,
    }));
  };

  const setItems = (field, items) => setDraft((prev) => ({ ...prev, [`${field}s`]: items }));

  const addItem = (field, items, types) => {
    if (!types.length) return;
    setItems(field, [
      { type: types[0].value, [field]: '', seq: nextSeq(items), publicDNC: false, dncPab: null },
      ...items,
    ]);
  };

  const removeItem = (field, items, index) => {
    const next = items.slice();
    next.splice(index, 1);
    setItems(field, next);
  };

  const changeItem = (field, items, index, name, value) => {
    const next = items.slice();
    next[index] = { ...next[index], [name]: value };
    setItems(field, next);
  };

  /* 'D' is My DNC; 'A' is PAB and must survive the toggle (see ContactItems). */
  const setDnc = (items, index, option) => {
    const dncPab = items[index].dncPab || '';
    const hasPab = dncPab.includes('A');
    let next = option === 'MY_DNC' ? 'D' : '';
    if (hasPab) next += 'A';
    changeItem('phone', items, index, 'dncPab', next || null);
  };

  return (
    <SimpleModal
      isOpen
      ignoreEscAndClickOutside
      uniqueId="contactInformationEdit"
      wrapperClassName={css.nestedWrapper}
      modalClassName={css.nestedModal}
      modalHeaderWrapperClassName={css.headerWrap}
      modalBodyClassName={css.bodyWrap}
      modalFooterWrapperClassName={css.nestedFooter}
      customHeader={<div className={css.nestedCaption}>Contact Information</div>}
      customFooter={
        <>
          <button type="button" className={css.btnGhost} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={classNames(css.btnPrimary, css.btnSave)}
            onClick={() => onSave(draft)}
          >
            Save
          </button>
        </>
      }
      modalBody={
        <div className={classNames(css.nestedBody, css.generalBody)}>
          <section className={css.ciSection}>
            <SectionHeader
              title="Phone Numbers"
              count={phones.length}
              info
              onAdd={() => addItem('phone', phones, PHONE_TYPES)}
            />
            {phones.map((p, index) => (
              <div className={css.ciRow} key={p.seq ?? index}>
                <TypeSelect
                  types={PHONE_TYPES}
                  value={p.type}
                  onChange={(name, value) => changeItem('phone', phones, index, name, value)}
                />
                <input
                  className={classNames(css.fieldInput, css.ciValue, {
                    [css.ciValueLocked]: p.publicDNC,
                  })}
                  name="phone"
                  type="text"
                  value={p.phone || ''}
                  disabled={p.publicDNC}
                  onChange={(ev) => changeItem('phone', phones, index, 'phone', ev.target.value)}
                />
                {p.publicDNC ? (
                  <div className={css.ciPublicDnc}>
                    <SVG icon="cp-national-dnc" className={css.ciPublicDncIcon} />
                    Public DNC
                  </div>
                ) : (
                  <div className={css.ciDnc}>
                    <Select
                      name="dncPab"
                      lists={DNC_OPTIONS}
                      selectedValue={(p.dncPab || '').includes('D') ? 'MY_DNC' : 'NO_DNC'}
                      valueKey="value"
                      labelKey="label"
                      autoFlip
                      onSelect={({ value }) => setDnc(phones, index, value)}
                    />
                  </div>
                )}
                <TrashButton onClick={() => removeItem('phone', phones, index)} />
              </div>
            ))}
          </section>

          <div className={css.ciDivider} />

          <section className={css.ciSection}>
            <SectionHeader
              title="Email Addresses"
              count={emails.length}
              onAdd={() => addItem('email', emails, EMAIL_TYPES)}
            />
            {emails.map((e, index) => (
              <div className={css.ciRow} key={e.seq ?? index}>
                <TypeSelect
                  types={EMAIL_TYPES}
                  value={e.type}
                  onChange={(name, value) => changeItem('email', emails, index, name, value)}
                />
                <input
                  className={classNames(css.fieldInput, css.ciEmail)}
                  name="email"
                  type="text"
                  value={e.email || ''}
                  onChange={(ev) => changeItem('email', emails, index, 'email', ev.target.value)}
                />
                <TrashButton onClick={() => removeItem('email', emails, index)} />
              </div>
            ))}
          </section>

          <div className={css.ciDivider} />

          <AddressBlock title="Property Address" prefix="" draft={draft} onChange={handleChange} />

          <div className={css.ciCheckRow}>
            <CheckboxItem
              name="mailAddressSame"
              label="Mailing Address is different from property address"
              checked={!draft.mailAddressSame}
              onChange={handleChange}
              labelClassName={css.ciCheckLabel}
            />
          </div>

          {draft.mailAddressSame ? null : (
            <AddressBlock
              title="Mailing Address"
              prefix="mail"
              draft={draft}
              onChange={handleChange}
            />
          )}
        </div>
      }
    />
  );
};

export default ContactInformationModal;
